import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiGrid,
  FiCalendar,
  FiActivity,
  FiAward,
  FiUser,
  FiSettings,
  FiLogOut,
  FiTarget,
  FiMessageSquare,
  FiVideo,
  FiX,
  FiChevronRight,
  FiLock
} from "react-icons/fi";
import { GiLotus } from "react-icons/gi";
import { logout, getAuth } from "../../utils/auth";
import { useCheckIn } from "../../context/CheckInContext";
import { userAPI } from "../../services/api";

export default function UserMobileDock() {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const { stats, unreadNotifCount } = useCheckIn();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [auth, setAuthState] = useState(getAuth);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const current = getAuth();
    setAuthState(current);
    if (current.token || current.user) {
      userAPI.getProfile().then(res => {
        if (res.data?.success && res.data.data) {
          setProfile(res.data.data);
        }
      }).catch(() => {});
    }
  }, []);

  // Close sheet on route change and auto-scroll active item into view
  useEffect(() => {
    setSheetOpen(false);
    if (scrollContainerRef.current) {
      const activeEl = scrollContainerRef.current.querySelector("[data-active='true']");
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    }
  }, [location.pathname]);

  const activeSub = profile?.activeSubscription;
  const isSubActive = activeSub && 
    (activeSub.status === "Active" || activeSub.status === "Approved") && 
    (new Date(activeSub.endDate) > new Date() || (typeof activeSub.daysLeft === "number" && activeSub.daysLeft > 0));

  const allowedFeatures = activeSub?.allowedFeatures || profile?.activeSubscription?.allowedFeatures || profile?.allowedFeatures;

  const isFeatureLocked = (featureKey, requireActiveSubOnly = false) => {
    const isAdmin = (auth.role || "").toLowerCase().includes("admin");
    if (isAdmin) return false;
    if (!isSubActive) return true;
    if (requireActiveSubOnly) return false;
    if (!featureKey) return false;
    if (allowedFeatures && Array.isArray(allowedFeatures)) {
      return !allowedFeatures.includes(featureKey);
    }
    return false;
  };

  const rawTier = activeSub?.packageName || profile?.tier || auth.user?.tier || auth.user?.subscriptionTier || "Free Trial";
  const lowTier = rawTier.toLowerCase().trim();
  let dynamicTierLabel = "Free Trial Yogi";
  if (lowTier.includes("ytt") || lowTier.includes("pro")) dynamicTierLabel = "YTT Pro Yogi";
  else if (lowTier.includes("premium")) dynamicTierLabel = "Premium Yogi";
  else if (lowTier.includes("standard")) dynamicTierLabel = "Standard Yogi";
  else if (lowTier.includes("basic")) dynamicTierLabel = "Basic Yogi";
  else if (rawTier && !lowTier.includes("free")) dynamicTierLabel = `${rawTier} Yogi`;

  const getInitials = () => {
    const name = profile?.name || auth.user?.name || auth.user?.fullName || auth.user?.userName || auth.user?.email || "Y";
    return name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "Y";
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  // Full Horizontal Scrollable Navigation Items
  const dockNavItems = [
    { id: "dashboard", label: "Home", path: "/user/dashboard", icon: FiGrid },
    { id: "livesession", label: "Live", path: "/user/livesession", icon: FiCalendar, isLive: true },
    { id: "dietplan", label: "Wellness", path: "/user/dietplan", icon: GiLotus },
    { id: "journey", label: "Journey", path: "/user/journey", icon: FiActivity },
    { id: "library", label: "Library", path: "/user/traininglibrary", icon: FiVideo },
    { id: "attendance", label: "Attendance", path: "/user/attendance", icon: FiActivity },
    { id: "goals", label: "Goals", path: "/user/goals", icon: FiTarget },
    { id: "achievements", label: "Awards", path: "/user/achievements", icon: FiAward },
    { id: "notification", label: "Alerts", path: "/user/notification", icon: FiMessageSquare, badge: unreadNotifCount || null },
    { id: "profile", label: "Profile", path: "/user/profile", icon: FiUser },
    { id: "settings", label: "Settings", path: "/user/settings", icon: FiSettings },
  ];

  // Secondary Modules inside the slide-up Bottom Sheet
  const sheetGroups = [
    {
      title: "Yoga & Practice",
      items: [
        { label: "Dashboard Overview", path: "/user/dashboard", icon: FiGrid },
        { label: "Live Sessions Room", path: "/user/livesession", icon: FiCalendar, isLive: true },
        { label: "Diet & Nutrition Plans", path: "/user/dietplan", icon: GiLotus, locked: isFeatureLocked(null, true) },
        { label: "My Journey Analytics", path: "/user/journey", icon: FiActivity, locked: isFeatureLocked("journey_analytics") },
        { label: "Training Video Library", path: "/user/traininglibrary", icon: FiVideo, locked: isFeatureLocked(null, true) },
        { label: "Daily Attendance Logs", path: "/user/attendance", icon: FiActivity, locked: isFeatureLocked("attendance_logs") },
        { label: "Personal Wellness Goals", path: "/user/goals", icon: FiTarget, locked: isFeatureLocked("goals_tracker") },
        { label: "Achievements & Badges", path: "/user/achievements", icon: FiAward, badge: stats?.streakDays ? `${stats.streakDays}d streak` : null, locked: isFeatureLocked("achievements") },
      ]
    },
    {
      title: "Account & Preferences",
      items: [
        { label: "Notifications History", path: "/user/notification", icon: FiMessageSquare, badge: unreadNotifCount ? `${unreadNotifCount} new` : null },
        { label: "Profile Information", path: "/user/profile", icon: FiUser },
        { label: "Account Settings", path: "/user/settings", icon: FiSettings },
      ]
    }
  ];

  return (
    <>
      {/* ── MOBILE SCROLLABLE BOTTOM DOCK (Strictly md:hidden) ── */}
      <nav
        aria-label="Mobile Navigation Dock"
        className="md:hidden fixed bottom-3 inset-x-3 z-[60] pointer-events-auto"
        style={{ paddingBottom: "max(0px, env(safe-area-inset-bottom))" }}
      >
        {/* Soft brand glow beneath the dock */}
        <div className="absolute inset-x-6 -bottom-1 h-10 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(242,154,12,0.35), transparent 70%)", filter: "blur(14px)" }} />

        {/* Gradient border ring */}
        <div className="relative rounded-[28px] p-[1.5px]"
          style={{
            background: "linear-gradient(120deg, rgba(242,154,12,0.85), rgba(255,255,255,0.9) 35%, rgba(255,255,255,0.9) 65%, rgba(107,138,90,0.75))",
            boxShadow: "0 18px 44px -8px rgba(60,35,5,0.28), 0 4px 14px rgba(0,0,0,0.06)",
          }}>
          <div className="relative flex items-center h-[66px] rounded-[26.5px] overflow-hidden"
            style={{
              background: "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,248,238,0.94))",
              backdropFilter: "blur(28px) saturate(190%)",
              WebkitBackdropFilter: "blur(28px) saturate(190%)",
              boxShadow: "inset 0 1px 0 #fff",
            }}>

            {/* Scrollable nav items */}
            <motion.div
              ref={scrollContainerRef}
              layoutScroll
              className="flex-1 min-w-0 h-full flex items-center gap-1 pl-2 pr-3 overflow-x-auto overflow-y-hidden"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 12px, #000 calc(100% - 18px), transparent 100%)",
                maskImage: "linear-gradient(90deg, transparent 0, #000 12px, #000 calc(100% - 18px), transparent 100%)",
              }}
            >
              {dockNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;

                return (
                  <motion.button
                    key={item.id}
                    data-active={isActive ? "true" : "false"}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setSheetOpen(false);
                      navigate(item.path);
                    }}
                    className="relative flex-shrink-0 min-w-[62px] h-[54px] px-2.5 rounded-[20px] flex flex-col items-center justify-center select-none border-0 outline-none bg-transparent"
                  >
                    {/* Sliding active pill */}
                    {isActive && (
                      <motion.div
                        layoutId="userDockActivePill"
                        transition={{ type: "spring", stiffness: 420, damping: 34 }}
                        className="absolute inset-0 rounded-[20px]"
                        style={{
                          background: "linear-gradient(135deg, #F29A0C 0%, #E97818 100%)",
                          boxShadow: "0 8px 20px -4px rgba(233,120,24,0.55), inset 0 1px 0 rgba(255,255,255,0.45)",
                        }}
                      >
                        {/* glossy highlight */}
                        <div className="absolute inset-x-2 top-1 h-3 rounded-full"
                          style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.35), transparent)" }} />
                      </motion.div>
                    )}

                    <motion.div
                      animate={{ y: isActive ? -1 : 0, scale: isActive ? 1.1 : 1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 22 }}
                      className="relative z-[1] flex items-center justify-center"
                    >
                      <Icon size={19} style={{ color: isActive ? "#fff" : "#7A6A58", filter: isActive ? "drop-shadow(0 2px 4px rgba(0,0,0,0.18))" : "none" }} />
                      {item.isLive && (
                        <span className="absolute -top-0.5 -right-1.5 flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 border-2 border-white"></span>
                        </span>
                      )}
                      {item.badge && !isActive && (
                        <span className="absolute -top-1.5 -right-2 px-1 min-w-[15px] h-[15px] bg-rose-500 text-white text-[8px] font-black rounded-full flex items-center justify-center border-2 border-white">
                          {item.badge > 9 ? "9+" : item.badge}
                        </span>
                      )}
                    </motion.div>
                    <span className="relative z-[1] text-[10px] tracking-tight mt-1 whitespace-nowrap leading-none"
                      style={{ color: isActive ? "#fff" : "#8A7A68", fontWeight: isActive ? 800 : 600 }}>
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </motion.div>

            {/* Divider */}
            <div className="w-px h-9 shrink-0" style={{ background: "linear-gradient(180deg, transparent, rgba(200,150,90,0.35), transparent)" }} />

            {/* Pinned Menu button */}
            <div className="shrink-0 px-2">
              <motion.button
                whileTap={{ scale: 0.88 }}
                onClick={() => setSheetOpen(!sheetOpen)}
                aria-label={sheetOpen ? "Close menu" : "Open menu"}
                className="relative w-[50px] h-[50px] rounded-[18px] flex items-center justify-center border-0 outline-none select-none"
                animate={{
                  background: sheetOpen
                    ? "linear-gradient(135deg, #F29A0C 0%, #E97818 100%)"
                    : "linear-gradient(135deg, #2A2118 0%, #1F1F1F 100%)",
                }}
                style={{ boxShadow: sheetOpen ? "0 8px 20px -4px rgba(233,120,24,0.6)" : "0 8px 20px -6px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.12)" }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.span
                    key={sheetOpen ? "close" : "open"}
                    initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.18 }}
                    className="flex items-center justify-center"
                  >
                    {sheetOpen
                      ? <FiX size={21} color="#fff" />
                      : <GiLotus size={23} color="#F29A0C" />}
                  </motion.span>
                </AnimatePresence>
                {!sheetOpen && unreadNotifCount > 0 && (
                  <>
                    <span className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-rose-500 animate-ping opacity-60" />
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-rose-500 text-white text-[9px] font-black rounded-full flex items-center justify-center border-2 border-white">
                      {unreadNotifCount > 9 ? "9+" : unreadNotifCount}
                    </span>
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── MOBILE SLIDE-UP DRAWER SHEET (Strictly md:hidden) ── */}
      <AnimatePresence>
        {sheetOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={() => setSheetOpen(false)}
              className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm"
            />

            {/* Bottom Sheet */}
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 360, damping: 32 }}
              className="absolute inset-x-0 bottom-0 max-h-[84vh] bg-[#FFF8EE] rounded-t-[34px] flex flex-col shadow-2xl overflow-hidden border-t border-white/80"
              style={{ paddingBottom: "max(84px, calc(env(safe-area-inset-bottom) + 82px))" }}
            >
              {/* Drag Handle & Header */}
              <div className="pt-3 pb-2.5 px-5 flex items-center justify-between border-b border-[#EFE3D2]/70">
                <div className="w-8" />
                <div className="w-12 h-1.5 rounded-full bg-stone-300 mx-auto" />
                <button
                  onClick={() => setSheetOpen(false)}
                  className="w-8 h-8 rounded-full bg-white border border-[#EFE3D2] flex items-center justify-center text-stone-600 active:scale-95"
                  aria-label="Close menu"
                >
                  <FiX size={16} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {/* User Card */}
                <div 
                  onClick={() => { setSheetOpen(false); navigate("/user/profile"); }}
                  className="flex items-center gap-3.5 p-3.5 rounded-[22px] bg-white border border-[#EFE3D2] shadow-sm cursor-pointer active:scale-[0.98] transition-all"
                >
                  <div className="w-12 h-12 rounded-[18px] bg-gradient-to-tr from-[#F29A0C] to-[#E97818] text-white font-black text-lg flex items-center justify-center shadow-md shrink-0">
                    {getInitials()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-stone-900 truncate">
                        {profile?.fullName || profile?.name || auth.user?.fullName || auth.user?.name || auth.user?.userName || "My Account"}
                      </h4>
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-800 shrink-0">
                        {dynamicTierLabel}
                      </span>
                    </div>
                    <p className="text-xs text-stone-500 truncate mt-0.5">
                      {profile?.email || auth.user?.email || ""}
                    </p>
                  </div>
                  <FiChevronRight className="text-stone-400 shrink-0" size={18} />
                </div>

                {/* Module Groups */}
                {sheetGroups.map((grp) => (
                  <div key={grp.title} className="space-y-1.5">
                    <p className="text-[10px] font-black uppercase tracking-wider text-stone-400 px-2">
                      {grp.title}
                    </p>
                    <div className="grid grid-cols-1 gap-1">
                      {grp.items.map((item) => {
                        const Icon = item.icon;
                        const isCurrent = location.pathname === item.path;
                        return (
                          <button
                            key={item.label}
                            onClick={() => {
                              setSheetOpen(false);
                              navigate(item.path);
                            }}
                            className={`flex items-center justify-between p-3 rounded-[18px] transition-all text-left ${
                              isCurrent 
                                ? 'bg-amber-500/15 text-[#C8660A] font-bold border border-amber-500/25' 
                                : 'bg-white hover:bg-stone-50 text-stone-700 font-medium border border-[#EFE3D2]/60'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isCurrent ? 'bg-[#F29A0C] text-white shadow-sm' : 'bg-stone-100 text-stone-600'}`}>
                                <Icon size={18} />
                              </div>
                              <span className="text-xs tracking-tight">{item.label}</span>
                            </div>

                            <div className="flex items-center gap-2">
                              {item.badge && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                  {item.badge}
                                </span>
                              )}
                              {item.locked && (
                                <span className="w-6 h-6 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center text-xs">
                                  <FiLock size={12} />
                                </span>
                              )}
                              <FiChevronRight className="text-stone-300" size={14} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Logout Button */}
                <div className="pt-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 p-3.5 rounded-[20px] bg-rose-500/10 text-rose-700 font-bold text-xs border border-rose-500/20 active:scale-[0.98] transition-all"
                  >
                    <FiLogOut size={16} />
                    <span>Log Out from Account</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
